import { lazy, type ReactNode } from 'react'
import { createBrowserRouter, Navigate, useLocation } from 'react-router'

import { LoginPage } from '../../features/auth'
import { useAuthStore } from '../../features/auth/hooks'
import { DashboardPage } from '../../features/dashboard'
import { EmptyState } from '../../shared/components'
import { useT } from '../../shared/i18n'
import { Shell } from '../layouts'

const EntriesPage = lazy(
  () => import('../../features/content-entries/EntriesPage'),
)

const EntryEditorPage = lazy(
  () => import('../../features/content-entries/EntryEditorPage'),
)

const ContentTypesPage = lazy(
  () => import('../../features/content-types/ContentTypesPage'),
)

const ContentTypeBuilderPage = lazy(
  () => import('../../features/content-types/ContentTypeBuilderPage'),
)

const MediaPage = lazy(() => import('../../features/media/MediaPage'))
const UsersPage = lazy(() => import('../../features/users/UsersPage'))
const RolesPage = lazy(() => import('../../features/roles/RolesPage'))
const PluginsPage = lazy(() => import('../../features/plugins/PluginsPage'))

const PluginDetailPage = lazy(
  () => import('../../features/plugins/PluginDetailPage'),
)

const PluginPageHost = lazy(
  () => import('../../extensions/slots/PluginPageHost'),
)

const AuditPage = lazy(() => import('../../features/audit/AuditPage'))

const MailSettingsPage = lazy(
  () => import('../../features/mail/MailSettingsPage'),
)

const WebsiteSettingsPage = lazy(
  () => import('../../features/website/WebsiteSettingsPage'),
)

const SettingsPage = lazy(() => import('../../features/settings/SettingsPage'))
const BackupPage = lazy(() => import('../../features/backup/BackupPage'))

const ImportStatusPage = lazy(
  () => import('../../features/backup/ImportStatusPage'),
)

function RequireAuth({ children }: { children: ReactNode }) {
  const status = useAuthStore((s) => s.status)
  const location = useLocation()

  if (status !== 'authenticated')
    return (
      <Navigate
        to="/login"
        replace
        state={{ from: location.pathname + location.search }}
      />
    )

  return <>{children}</>
}

function RequirePermission({
  permission,
  children,
}: {
  permission: string
  children: ReactNode
}) {
  const allowed = useAuthStore((s) => s.permissions.has(permission))
  const { t } = useT()

  return allowed ? (
    <>{children}</>
  ) : (
    <EmptyState title={t.shell.accessDenied}>
      {t.shell.accessDeniedText}
    </EmptyState>
  )
}

function NotFound() {
  const { t } = useT()

  return (
    <EmptyState title={t.shell.notFound}>{t.shell.notFoundText}</EmptyState>
  )
}

const guard = (permission: string, node: ReactNode) => (
  <RequirePermission permission={permission}>{node}</RequirePermission>
)

export const router = createBrowserRouter([
  { path: '/login', element: <LoginPage /> },
  // Backup import progress (ADR-077): the import signs everybody out.
  { path: '/backup-import/:id', element: <ImportStatusPage /> },
  {
    path: '/',
    element: (
      <RequireAuth>
        <Shell />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      {
        path: 'content',
        handle: { crumb: 'content' },
        children: [
          {
            index: true,
            element: guard('content.entry.read', <EntriesPage />),
          },
          {
            path: 'new',
            element: guard('content.entry.create', <EntryEditorPage />),
            handle: { crumb: 'newEntry' },
          },
          {
            path: ':id',
            element: guard('content.entry.read', <EntryEditorPage />),
            handle: { crumb: 'edit' },
          },
        ],
      },
      {
        path: 'content-types',
        handle: { crumb: 'contentTypes' },
        children: [
          {
            index: true,
            element: guard('content.type.read', <ContentTypesPage />),
          },
          {
            path: ':key',
            element: guard('content.type.read', <ContentTypeBuilderPage />),
            handle: { crumb: 'builder' },
          },
        ],
      },
      {
        path: 'media',
        element: guard('media.read', <MediaPage />),
        handle: { crumb: 'media' },
      },
      {
        path: 'users',
        element: guard('user.read', <UsersPage />),
        handle: { crumb: 'users' },
      },
      {
        path: 'roles',
        element: guard('user.read', <RolesPage />),
        handle: { crumb: 'roles' },
      },
      {
        path: 'plugins',
        handle: { crumb: 'plugins' },
        children: [
          { index: true, element: guard('plugin.read', <PluginsPage />) },
          {
            path: ':key',
            element: guard('plugin.read', <PluginDetailPage />),
            handle: { crumb: 'details' },
          },
        ],
      },
      {
        path: 'extensions/:pluginKey/:pageId/*',
        element: <PluginPageHost />,
        handle: { crumb: 'extension' },
      },
      {
        path: 'audit',
        element: guard('audit.read', <AuditPage />),
        handle: { crumb: 'audit' },
      },
      {
        path: 'website',
        element: guard('site.settings.manage', <WebsiteSettingsPage />),
        handle: { crumb: 'website' },
      },
      {
        path: 'mail',
        element: guard('mail.settings.manage', <MailSettingsPage />),
        handle: { crumb: 'mail' },
      },
      {
        path: 'backup',
        element: guard('backup.export', <BackupPage />),
        handle: { crumb: 'backup' },
      },
      {
        path: 'settings',
        element: <SettingsPage />,
        handle: { crumb: 'settings' },
      },
      {
        path: '*',
        element: <NotFound />,
      },
    ],
  },
])
